import { useState } from "react";
import { Card, CardHeader, IconButton, Typography, Box, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { Edit, Delete, MoreVert } from "@mui/icons-material";

/**
 * A card component that displays a list of cards with optional actions.
 * @param {Object} props Component props.
 * @param {Array} props.cardData An array of objects with title and subheader properties.
 * @param {Function} props.onEditClick Called when the edit action is clicked.
 * @param {Function} props.onDelete Called when the delete action is clicked.
 * @param {Function} props.onCardClick Called when a card is clicked.
 * @param {boolean} props.showMenu Determines if the action menu should be shown.
 * @returns {ReactElement} The rendered component.
 */
export const ActionCard = ({
  cardData = [],
  onEditClick = () => {},
  onDelete = () => {},
  onCardClick = () => {},
  showMenu = false,
}) => {
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);

  const handleActions = (event, index) => {
    event.stopPropagation();
    setOpenMenu(event.currentTarget);
    setSelectedCard(cardData[index]);
  };

  const handleClose = () => {
    setOpenMenu(null);
    setSelectedCard(null);
  };

  const handleEdit = () => {
    onEditClick(selectedCard);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(selectedCard);
    handleClose();
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          p: 3,
          flexWrap: "wrap",
        }}
      >
        {cardData.map((item, index) => (
          <Card
            key={index}
            onClick={() => onCardClick?.(index)}
            sx={{ flex: "0 0 calc(25% - 16px)", borderRadius: 3, cursor: "pointer" }}
          >
            <CardHeader
              action={
                showMenu && (
                  <IconButton
                    aria-label="settings"
                    onClick={(e) => {
                      handleActions(e, index);
                    }}
                  >
                    <MoreVert />
                  </IconButton>
                )
              }
              title={
                <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
                  {item.title}
                </Typography>
              }
              subheader={
                <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                  {item.subheader}
                </Typography>
              }
            />
          </Card>
        ))}
      </Box>

      <Menu anchorEl={openMenu} open={Boolean(openMenu)} onClose={handleClose}>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
